import { action } from '@ember/object';
import { trackedSet } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { PrismTable, styleEntry } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Hotelinfo } from 'frontend-utils/models';
import GeneralTablesCellsThIconWithTooltip from 'lumen/components/general/tables/cells/th-icon-with-tooltip';
import TdCompetitorRateMonth from 'lumen/components/hotel/pricing-assistant/table/cells/td-competitor-rate-month';
import TdDateMonth from 'lumen/components/hotel/pricing-assistant/table/cells/td-date-month';
import TdHotelOccupancyMonth, {
  type HotelOccupancyMonthColumn,
} from 'lumen/components/hotel/pricing-assistant/table/cells/td-hotel-occupancy-month';
import TdHotelPickUpMonth from 'lumen/components/hotel/pricing-assistant/table/cells/td-hotel-pick-up-month';
import TdLeftToSellMonth from 'lumen/components/hotel/pricing-assistant/table/cells/td-left-to-sell-month';
import TdOverbookingMonth from 'lumen/components/hotel/pricing-assistant/table/cells/td-overbooking-month';
import TdRateGroupsMonth, {
  type RateGroupsMonthColumn,
} from 'lumen/components/hotel/pricing-assistant/table/cells/td-rate-groups-month';
import TdRestrictionsMonth from 'lumen/components/hotel/pricing-assistant/table/cells/td-restrictions-month';
import TdRoomRateMonth from 'lumen/components/hotel/pricing-assistant/table/cells/td-room-rate-month';
import TdRoomsMonth, {
  type RoomsMonthColumn,
} from 'lumen/components/hotel/pricing-assistant/table/cells/td-rooms-month';
import ThBaseRoomMonth from 'lumen/components/hotel/pricing-assistant/table/cells/th-base-room-month';
import ThCompetitorMonth from 'lumen/components/hotel/pricing-assistant/table/cells/th-competitor-month';
import HotelPricingAssistantTableModalsEditOverrideModalEditOverrideModal from 'lumen/components/hotel/pricing-assistant/table/modals/edit-override-modal/edit-override-modal';
import HotelPricingAssistantTableOverrideLegend from 'lumen/components/hotel/pricing-assistant/table/override-legend';
import type {
  TablePricingManagerContext,
  TablePricingManagerContextColumn,
} from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import type { Carate, Period, Rate } from 'lumen/models';
import type { RateGroup } from 'lumen/models/pa-subscription-meta-info';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { isWeekendDate } from 'lumen/utils/charts/chart-utils';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { DAY_TABLE_SIZES } from 'lumen/utils/constants/general-constants';
import { PRICING_TABLE_RESERVED_HEIGHT } from 'lumen/utils/constants/pricing-assistant-constants';
import { sortRoomCaratesByOffsetHierarchy } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import styles from './table-pricing-manager-month.module.css';

const DATE_COLUMN_WIDTH = 192;

// Resolves to nothing on every row, so the cell renders empty
const PLACEHOLDER_VALUE_PATH = 'placeholder';

interface MonthRow extends CustomEmberTableRow {
  className?: string;
  competitors: Record<string, Rate | undefined>;
  id: string;
  period: Period;
  rooms: Record<string, Carate | undefined>;
}

/* A column whose cell reads the period. A room group's column also carries the
   room's PMS id, so the cell reads that room's values, and the room name for the
   left to sell tooltip, as a row can lack the room's carate. */
export interface PeriodMonthColumn extends CustomEmberTableColumn {
  pmsRoomId?: string;
  roomName?: string;
}

interface HotelPricingAssistantTableTablePricingManagerMonthSignature {
  Args: {
    areRoomsLoading: boolean;
    competitorsForCurrentHotelAndOTA: Hotelinfo[] | null;
    competitorsPeriods: Period[];
    flattenedRoomOffsetHierarchy: string[];
    isLoading: boolean;
    onDayHeaderClick?: (period: Period) => void;
    periods: Period[];
    rateCode: string | null;
    refDaysAgo: number;
  };
}

export default class HotelPricingAssistantTableTablePricingManagerMonth extends Component<HotelPricingAssistantTableTablePricingManagerMonthSignature> {
  @service declare access: Services['access'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];
  @service
  declare pricingAssistantHandleRecommendationActionsService: Services['pricing-assistant-handle-recommendation-actions-service'];

  @tracked carateToEditOverride: Carate | null = null;

  datesInLoading = trackedSet<string>();

  get baseRoomName(): string | null | undefined {
    return this.hotelAndMarketService.hotel.caxxxPmsSettings
      .pms_leading_room_id;
  }

  /* Header rows: ember-table renders a second row for `subcolumns`. The date
     column has none, so it spans both rows. */
  get columns(): CustomEmberTableColumn[] {
    const columns: CustomEmberTableColumn[] = [
      {
        name: this.intl.t('general.date'),
        valuePath: 'period',
        isFixed: 'left',
        hasRightBorder: true,
        textAlign: 'left',
        width: DATE_COLUMN_WIDTH,
        tdComponent: TdDateMonth,
        tdClassNames: styles['date-cell'],
        onDayHeaderClick: this.args.onDayHeaderClick,
      },
      this.groupColumn(
        this.intl.t('pricing_manager.monthly_table.hotel_level'),
        [
          ...this.occupancyColumns,
          this.leftToSellColumn(),
          this.periodColumn(
            this.intl.t('pricing_manager.monthly_table.overbookings'),
            TdOverbookingMonth,
          ),
          {
            ...this.periodColumn(
              this.intl.t('pricing_manager.monthly_table.restrictions'),
              TdRestrictionsMonth,
            ),
            rateGroups: this.configuredRateGroups,
          },
        ],
      ),
    ];

    if (this.args.areRoomsLoading) {
      columns.push(this.roomsPlaceholderColumn());
    }

    const baseRoomIndex = this.roomCarates.findIndex(
      (carate) => carate.roomName === this.baseRoomName,
    );

    const baseRoomCarate = this.roomCarates[baseRoomIndex];

    if (baseRoomCarate) {
      columns.push(this.baseRoomGroupColumn(baseRoomCarate, baseRoomIndex));
    }

    this.roomCarates.forEach((carate, index) => {
      if (index !== baseRoomIndex) {
        columns.push(
          this.groupColumn(carate.roomName, [
            this.leftToSellColumn(carate),
            this.roomRateColumn(index),
          ]),
        );
      }
    });

    const restrictionColumns: CustomEmberTableColumn[] = [
      this.roomsColumn(this.roomCarates),
    ];

    if (this.configuredRateGroups.length > 0) {
      restrictionColumns.push(this.rateGroupsColumn(this.configuredRateGroups));
    }

    columns.push(
      this.groupColumn(
        this.intl.t('pricing_manager.monthly_table.restrictions'),
        restrictionColumns,
      ),
    );

    return columns;
  }

  get competitorRateColumns(): CustomEmberTableColumn[] {
    return (this.args.competitorsForCurrentHotelAndOTA ?? []).map(
      (competitor) => this.competitorRateColumn(competitor),
    );
  }

  @cached
  get competitorRatesByDate(): Record<string, Record<string, Rate>> {
    return Object.fromEntries(
      this.args.competitorsPeriods.map((period) => [
        period.fromDate.format('YYYY-MM-DD'),
        Object.fromEntries(
          period.competitorRates.map((rate) => [rate.hotelinfo.id, rate]),
        ) as Record<string, Rate>,
      ]),
    );
  }

  /* The same gates under which the week view's day tooltip lists rate groups. */
  get configuredRateGroups(): RateGroup[] {
    if (
      !this.access.hasPricingAssistantMinLosRestrictionsFeature ||
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_DEEP_DISCOUNT,
      )
    ) {
      return [];
    }

    return this.pricingAssistantDataRequestService.configuredRateGroups;
  }

  /* My OTB, Forecast and Pick-up, under the same gates as the week view's day header and day tooltip. */
  get occupancyColumns(): CustomEmberTableColumn[] {
    if (!this.access.hasFeature('pms.otb.view')) {
      return [];
    }

    const columns: CustomEmberTableColumn[] = [
      this.occupancyColumn(this.intl.t('pricing_assistant.my_OTB'), false),
    ];

    if (this.access.hasOTBForecastView) {
      columns.push(this.occupancyColumn(this.intl.t('general.forecast'), true));
    }

    columns.push(this.pickUpColumn(this.args.periods));

    return columns;
  }

  /* Rooms with a carate for the selected rate code on any day in the range, so a room missing on the first day still gets its columns.
     None while they load, when the Rooms placeholder stands in for them. */
  @cached
  get roomCarates(): Carate[] {
    if (this.args.areRoomsLoading) {
      return [];
    }

    const caratesByRoomName = new Map<string, Carate>();

    for (const period of this.args.periods) {
      for (const carate of period.ownHotelCarates) {
        if (
          carate.rateCode === this.args.rateCode &&
          !caratesByRoomName.has(carate.roomName)
        ) {
          caratesByRoomName.set(carate.roomName, carate);
        }
      }
    }

    return sortRoomCaratesByOffsetHierarchy(
      Array.from(caratesByRoomName.values()),
      this.args.flattenedRoomOffsetHierarchy,
    );
  }

  @cached
  get rows(): MonthRow[] {
    if (this.args.isLoading) {
      return [];
    }

    return this.args.periods.map((period): MonthRow => {
      const id = period.fromDate.format('YYYY-MM-DD');
      const ownHotelCarates = period.ownHotelCarates;
      const rooms: MonthRow['rooms'] = {};

      this.roomCarates.forEach((roomCarate, index) => {
        rooms[index] = ownHotelCarates.find(
          (carate) =>
            carate.roomName === roomCarate.roomName &&
            carate.rateCode === this.args.rateCode,
        );
      });

      return {
        // TODO(COA-4308): temporary Fri/Sat weekend, mirroring Rate Insight's isWeekendDate.
        // Replace with the hotel's weekend-day mapping from Settings, per spec:
        // https://www.figma.com/design/yx8TkjqaRngRguX0kSrJab/Multi-rate-table---Extended-view?node-id=1-17041&m=dev
        className: isWeekendDate(period.fromDate)
          ? styles['weekend-row']
          : undefined,
        competitors: this.competitorRatesByDate[id] ?? {},
        id,
        period,
        rooms,
      };
    });
  }

  @cached
  get tableContext(): TablePricingManagerContext {
    return {
      datesInLoading: this.datesInLoading,
      deleteOverride: (carate) => this.deleteOverride(carate),
      openEditOverrideModal: (carate) => this.openEditOverrideModal(carate),
      refDaysAgo: this.args.refDaysAgo,
    };
  }

  baseRoomGroupColumn(carate: Carate, index: number): CustomEmberTableColumn {
    return {
      ...this.groupColumn(carate.roomName, [
        this.leftToSellColumn(carate),
        ...this.overbookingAndRestrictionColumns(carate),
        this.roomRateColumn(index),
        ...this.competitorRateColumns,
      ]),
      thComponent: ThBaseRoomMonth,
    };
  }

  competitorRateColumn(competitor: Hotelinfo): CustomEmberTableColumn {
    return {
      ...this.valueColumn(
        competitor.name,
        `competitors.${competitor.id!}`,
        TdCompetitorRateMonth,
      ),
      thComponent: ThCompetitorMonth,
    };
  }

  groupColumn(
    name: string,
    subcolumns: CustomEmberTableColumn[],
  ): CustomEmberTableColumn {
    const lastSubcolumn = subcolumns[subcolumns.length - 1];

    if (lastSubcolumn) {
      lastSubcolumn.hasRightBorder = true;
    }

    return {
      name,
      valuePath: '',
      textAlign: 'center',
      hasRightBorder: true,
      subcolumns,
    };
  }

  // Left to sell of this room, or of the hotel without one
  leftToSellColumn(carate?: Carate): PeriodMonthColumn {
    return this.periodColumn(
      this.intl.t('pricing_manager.monthly_table.left_to_sell'),
      TdLeftToSellMonth,
      carate,
    );
  }

  occupancyColumn(
    name: string,
    isForecast: boolean,
  ): HotelOccupancyMonthColumn {
    return {
      ...this.periodColumn(name, TdHotelOccupancyMonth),
      isForecast,
    };
  }

  // Overbookings and Restrictions of this room, or of the rooms placeholder without one
  overbookingAndRestrictionColumns(carate?: Carate): PeriodMonthColumn[] {
    return [
      this.periodColumn(
        this.intl.t('pricing_manager.monthly_table.overbookings'),
        TdOverbookingMonth,
        carate,
      ),
      this.periodColumn(
        this.intl.t('pricing_manager.monthly_table.restrictions'),
        TdRestrictionsMonth,
        carate,
      ),
    ];
  }

  periodColumn(
    name: string,
    tdComponent: CustomEmberTableColumn['tdComponent'],
    carate?: Carate,
  ): PeriodMonthColumn {
    return {
      ...this.valueColumn(name, 'period', tdComponent),
      pmsRoomId: carate?.pmsRoomtype,
      roomName: carate?.roomName,
    };
  }

  /* The header names the first date's comparison (`Period.referenceChangeDays`), as the week view's day tooltip does. */
  pickUpColumn(periods: Period[]): CustomEmberTableColumn {
    const [firstPeriod] = periods;

    return {
      ...this.periodColumn(
        this.intl.t('pricing_manager.monthly_table.pickup_rooms'),
        TdHotelPickUpMonth,
      ),
      thComponent: GeneralTablesCellsThIconWithTooltip,
      tooltip: {
        content: firstPeriod
          ? this.intl.t('general.daysago', {
              count: firstPeriod.referenceChangeDays,
            })
          : '',
      },
    };
  }

  rateGroupsColumn(rateGroups: RateGroup[]): RateGroupsMonthColumn {
    return {
      name: this.intl.t('pricing_assistant.stay_restrictions.rate_groups'),
      valuePath: 'period',
      textAlign: 'right',
      width: DAY_TABLE_SIZES.VALUE_WIDTH,
      thClassNames: styles['rate-header'],
      tdComponent: TdRateGroupsMonth,
      rateGroups,
    };
  }

  // Keyed by index: room names can contain dots, which a value path would split
  roomRateColumn(index: number): CustomEmberTableColumn {
    return this.valueColumn(
      this.intl.t('pricing_manager.monthly_table.room_rate'),
      `rooms.${index}`,
      TdRoomRateMonth,
    );
  }

  roomsColumn(rooms: Carate[]): RoomsMonthColumn {
    return {
      name: this.intl.t('pricing_assistant.label.rooms'),
      valuePath: 'period',
      textAlign: 'right',
      width: DAY_TABLE_SIZES.VALUE_WIDTH,
      thClassNames: styles['rate-header'],
      tdComponent: TdRoomsMonth,
      rooms,
    };
  }

  /* Stands in for the room groups while they load, in the base room's shape so the columns stay put */
  roomsPlaceholderColumn(): CustomEmberTableColumn {
    return this.groupColumn(this.intl.t('pricing_assistant.label.rooms'), [
      this.leftToSellColumn(),
      ...this.overbookingAndRestrictionColumns(),
      this.valueColumn(
        this.intl.t('pricing_manager.monthly_table.room_rate'),
        PLACEHOLDER_VALUE_PATH,
        TdRoomRateMonth,
      ),
      ...this.competitorRateColumns,
    ]);
  }

  valueColumn(
    name: string,
    valuePath: string,
    tdComponent: CustomEmberTableColumn['tdComponent'],
  ): CustomEmberTableColumn {
    return {
      name,
      valuePath,
      textAlign: 'right',
      width: DAY_TABLE_SIZES.VALUE_WIDTH,
      thClassNames: styles['rate-header'],
      tdComponent,
      tableContext: this.tableContext,
    } satisfies CustomEmberTableColumn & TablePricingManagerContextColumn;
  }

  @action
  closeEditOverrideModal(): void {
    this.carateToEditOverride = null;
  }

  @action
  async deleteOverride(carate: Carate): Promise<void> {
    if (!carate.override || typeof carate.override.id !== 'string') {
      return;
    }

    const dateString = carate.period.fromDate.format('YYYY-MM-DD');

    this.datesInLoading.add(dateString);

    try {
      await this.pricingAssistantHandleRecommendationActionsService.handleDeleteOverride(
        carate,
      );
    } finally {
      this.datesInLoading.delete(dateString);
    }
  }

  @action
  openEditOverrideModal(carate: Carate): void {
    this.carateToEditOverride = carate;
  }

  <template>
    <HotelPricingAssistantTableOverrideLegend />

    <div
      class={{styles.table}}
      style={{styleEntry
        "--table-reserved-height"
        PRICING_TABLE_RESERVED_HEIGHT
      }}
    >
      <PrismTable
        @columns={{this.columns}}
        @isLoading={{@isLoading}}
        @nrOfEmptyRows={{31}}
        @nrOfLoadingRows={{5}}
        @rows={{this.rows}}
        @widthConstraint="gte-container-slack"
      />
    </div>

    {{#if this.carateToEditOverride}}
      <HotelPricingAssistantTableModalsEditOverrideModalEditOverrideModal
        @carate={{this.carateToEditOverride}}
        @onCloseModal={{this.closeEditOverrideModal}}
        @rateCode={{@rateCode}}
        @rooms={{this.roomCarates}}
      />
    {{/if}}
  </template>
}
